import React, { useState } from "react";
import PropTypes from "prop-types";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { FloatLabel } from "primereact/floatlabel";
import { Message } from "primereact/message";
import { useToast } from "../ToastProvider";
import {
  formatDocument,
  cleanDocument,
  validateDocument,
} from "../../utils/documentUtils";
import "./RouterRegistrationDialog.css";

/**
 * RouterRegistrationDialog
 * XPI-specific router access registration dialog
 * Allows users without router access to submit registration request
 */
const RouterRegistrationDialog = ({ visible, onHide, onSubmit }) => {
  const { showError } = useToast();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    emailConfirm: "",
    cpf: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Remove non-digits
    const cleanPhone = phone.replace(/\D/g, "");

    // Check for valid phone number (international support)
    // Minimum: 10 digits (Brazilian landline without country code)
    // Maximum: 15 digits (E.164 international standard)
    // Common formats:
    // - 10 digits: Brazilian landline (11) 3333-4444
    // - 11 digits: Brazilian mobile (11) 98765-4321 or USA +1 (XXX) XXX-XXXX
    // - 12 digits: Brazilian landline with country code +55 11 3333-4444
    // - 13 digits: Brazilian mobile with country code +55 11 98765-4321
    return cleanPhone.length >= 10 && cleanPhone.length <= 15;
  };

  const validateForm = () => {
    const newErrors = {};

    // Full Name
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Nome completo é obrigatório";
    } else if (formData.fullName.trim().split(" ").length < 2) {
      newErrors.fullName = "Digite seu nome completo";
    }

    // Email
    if (!formData.email.trim()) {
      newErrors.email = "Email é obrigatório";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Email inválido";
    }

    // Email Confirmation
    if (!formData.emailConfirm.trim()) {
      newErrors.emailConfirm = "Confirmação de email é obrigatória";
    } else if (formData.email !== formData.emailConfirm) {
      newErrors.emailConfirm = "Os emails não coincidem";
    }

    // CPF / CNPJ
    if (!formData.cpf.trim()) {
      newErrors.cpf = "CPF ou CNPJ é obrigatório";
    } else {
      const docResult = validateDocument(formData.cpf);
      if (!docResult.valid) {
        newErrors.cpf = docResult.error;
      }
    }

    // Phone
    if (!formData.phone.trim()) {
      newErrors.phone = "Telefone é obrigatório";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Telefone inválido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Remove formatting from CPF/CNPJ and phone before sending
      const cleanedData = {
        ...formData,
        cpf: cleanDocument(formData.cpf),
        phone: formData.phone.replace(/\D/g, ""),
      };

      await onSubmit(cleanedData);
      // Reset form on success
      setFormData({
        fullName: "",
        email: "",
        emailConfirm: "",
        cpf: "",
        phone: "",
      });
      setErrors({});
    } catch (error) {
      // Show error as toast instead of inline message
      showError(error.message || "Erro ao enviar solicitação", "Erro");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPhone = (value) => {
    // Preserve leading +
    const hasPlus = value.startsWith("+");

    // Remove all non-digits (keep only numbers)
    const numbers = value.replace(/\D/g, "");

    if (!numbers) {
      return hasPlus ? "+" : "";
    }

    // Detect country code
    let countryCode = "";
    let phoneNumbers = numbers;

    if (hasPlus) {
      // Has + symbol - detect country code
      if (numbers.startsWith("55")) {
        // Brazil: +55 (XX) XXXXX-XXXX or +55 (XX) XXXX-XXXX
        countryCode = "55";
        phoneNumbers = numbers.slice(2, 13); // Limit to 11 digits after country code
      } else if (numbers.startsWith("1")) {
        // USA/Canada: +1 (XXX) XXX-XXXX
        countryCode = "1";
        phoneNumbers = numbers.slice(1, 11); // Limit to 10 digits after country code
      } else if (numbers.length >= 2) {
        // Other country codes - assume 1-3 digit country code
        // Try to intelligently detect where country code ends
        // Most country codes are 1-3 digits
        if (numbers.length <= 3) {
          // Just country code so far
          countryCode = numbers;
          phoneNumbers = "";
        } else {
          // Has country code + phone number
          // Default to 2-digit country code for unknown codes
          countryCode = numbers.slice(0, 2);
          phoneNumbers = numbers.slice(2, 17); // Limit to 15 digits after
        }
      } else {
        // Just one digit after +
        countryCode = numbers;
        phoneNumbers = "";
      }
    } else if (numbers.length > 11) {
      // No + but more than 11 digits - might have country code
      if (numbers.startsWith("55") && numbers.length >= 12) {
        // Brazil with country code but no +
        countryCode = "55";
        phoneNumbers = numbers.slice(2, 13);
      } else if (numbers.startsWith("1") && numbers.length >= 11) {
        // USA with country code but no +
        countryCode = "1";
        phoneNumbers = numbers.slice(1, 11);
      } else {
        // Unknown - just limit to 15 digits total
        phoneNumbers = numbers.slice(0, 15);
      }
    } else {
      // No + and 11 or fewer digits - treat as local number (Brazil)
      phoneNumbers = numbers.slice(0, 11);
    }

    // Format based on country code
    let formatted = "";

    if (countryCode === "1") {
      // USA/Canada format: (XXX) XXX-XXXX
      if (phoneNumbers.length <= 3) {
        formatted = phoneNumbers;
      } else if (phoneNumbers.length <= 6) {
        formatted = `(${phoneNumbers.slice(0, 3)}) ${phoneNumbers.slice(3)}`;
      } else {
        formatted = `(${phoneNumbers.slice(0, 3)}) ${phoneNumbers.slice(3, 6)}-${phoneNumbers.slice(6, 10)}`;
      }
    } else if (countryCode === "55" || !countryCode) {
      // Brazil format: (XX) XXXXX-XXXX or (XX) XXXX-XXXX
      if (phoneNumbers.length <= 2) {
        formatted = phoneNumbers;
      } else if (phoneNumbers.length <= 6) {
        formatted = `(${phoneNumbers.slice(0, 2)}) ${phoneNumbers.slice(2)}`;
      } else if (phoneNumbers.length <= 10) {
        formatted = `(${phoneNumbers.slice(0, 2)}) ${phoneNumbers.slice(2, 6)}-${phoneNumbers.slice(6)}`;
      } else {
        formatted = `(${phoneNumbers.slice(0, 2)}) ${phoneNumbers.slice(2, 7)}-${phoneNumbers.slice(7)}`;
      }
    } else {
      // Other country codes - generic format (no special formatting)
      formatted = phoneNumbers;
    }

    // Build final result
    if (countryCode) {
      return `+${countryCode}${formatted ? " " + formatted : ""}`;
    } else if (hasPlus) {
      return `+${formatted}`;
    }

    return formatted;
  };

  const handleChange = (field, value) => {
    let processedValue = value;

    // Convert email fields to lowercase
    if (field === "email" || field === "emailConfirm") {
      processedValue = value.toLowerCase();
    }
    // Capitalize first letter of each word for full name
    else if (field === "fullName") {
      processedValue = value
        .split(" ")
        .map((word) => {
          if (word.length === 0) return word;
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(" ");
    }
    // Apply CPF/CNPJ mask
    else if (field === "cpf") {
      processedValue = formatDocument(value);
    }
    // Apply phone mask
    else if (field === "phone") {
      processedValue = formatPhone(value);
    }

    setFormData((prev) => ({ ...prev, [field]: processedValue }));
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleDialogHide = () => {
    // Reset form when dialog closes
    setFormData({
      fullName: "",
      email: "",
      emailConfirm: "",
      cpf: "",
      phone: "",
    });
    setErrors({});
    setIsSubmitting(false);
    onHide();
  };

  const footer = (
    <div className="router-registration-footer">
      <Button
        label="Cancelar"
        icon="pi pi-times"
        onClick={handleDialogHide}
        className="p-button-text"
        disabled={isSubmitting}
        data-evo-id="router-registration-btn-cancel"
      />
      <Button
        label="Enviar Solicitação"
        icon="pi pi-check"
        onClick={handleSubmit}
        disabled={isSubmitting}
        loading={isSubmitting}
        data-evo-id="router-registration-btn-submit"
      />
    </div>
  );

  return (
    <Dialog
      header="Solicitação de Acesso ao Roteador"
      visible={visible}
      style={{ width: "90vw", maxWidth: "550px", maxHeight: "90vh" }}
      onHide={handleDialogHide}
      footer={footer}
      modal
      draggable={false}
      className="router-registration-dialog"
      data-evo-id="router-registration-dialog"
      contentStyle={{ maxHeight: "calc(90vh - 180px)", overflowY: "auto" }}
    >
      <div className="router-registration-content">
        <Message
          severity="info"
          text="Você ainda não possui acesso ao roteador. Preencha o formulário abaixo para solicitar acesso."
          className="router-registration-message"
          style={{ marginBottom: "1.5rem" }}
        />

        <div className="p-field" style={{ marginBottom: "1.5rem" }}>
          <FloatLabel>
            <InputText
              id="fullName"
              value={formData.fullName}
              onChange={(e) => handleChange("fullName", e.target.value)}
              className={errors.fullName ? "p-invalid" : ""}
              style={{ width: "100%" }}
              disabled={isSubmitting}
              data-evo-id="router-registration-input-fullname"
            />
            <label htmlFor="fullName">Nome Completo *</label>
          </FloatLabel>
          {errors.fullName && (
            <small className="p-error">{errors.fullName}</small>
          )}
        </div>

        <div className="p-field" style={{ marginBottom: "1.5rem" }}>
          <FloatLabel>
            <InputText
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className={errors.email ? "p-invalid" : ""}
              style={{ width: "100%" }}
              disabled={isSubmitting}
              data-evo-id="router-registration-input-email"
            />
            <label htmlFor="email">Email *</label>
          </FloatLabel>
          {errors.email && <small className="p-error">{errors.email}</small>}
        </div>

        <div className="p-field" style={{ marginBottom: "1.5rem" }}>
          <FloatLabel>
            <InputText
              id="emailConfirm"
              type="email"
              value={formData.emailConfirm}
              onChange={(e) => handleChange("emailConfirm", e.target.value)}
              className={errors.emailConfirm ? "p-invalid" : ""}
              style={{ width: "100%" }}
              disabled={isSubmitting}
              data-evo-id="router-registration-input-email-confirm"
            />
            <label htmlFor="emailConfirm">Confirme o Email *</label>
          </FloatLabel>
          {errors.emailConfirm && (
            <small className="p-error">{errors.emailConfirm}</small>
          )}
        </div>

        <div className="p-field" style={{ marginBottom: "1.5rem" }}>
          <FloatLabel>
            <InputText
              id="cpf"
              value={formData.cpf}
              onChange={(e) => handleChange("cpf", e.target.value)}
              className={errors.cpf ? "p-invalid" : ""}
              style={{ width: "100%" }}
              disabled={isSubmitting}
              data-evo-id="router-registration-input-cpf"
            />
            <label htmlFor="cpf">CPF / CNPJ *</label>
          </FloatLabel>
          {errors.cpf && <small className="p-error">{errors.cpf}</small>}
        </div>

        <div className="p-field" style={{ marginBottom: "1rem" }}>
          <FloatLabel>
            <InputText
              id="phone"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              className={errors.phone ? "p-invalid" : ""}
              style={{ width: "100%" }}
              disabled={isSubmitting}
              data-evo-id="router-registration-input-phone"
              placeholder="+55 (11) 98765-4321"
            />
            <label htmlFor="phone">Telefone *</label>
          </FloatLabel>
          {errors.phone && <small className="p-error">{errors.phone}</small>}
        </div>

        <div className="router-registration-info">
          <p>
            Após enviar a solicitação, você receberá um email com instruções
            para completar o acesso ao roteador.
          </p>
        </div>
      </div>
    </Dialog>
  );
};

RouterRegistrationDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default RouterRegistrationDialog;
